import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { LoadingOutlined, ArrowLeftOutlined, InfoCircleOutlined, FullscreenOutlined, FullscreenExitOutlined } from "@ant-design/icons";
import { servicesService } from "../../services";
import { useAuth } from "../../contexts/AuthContext";

// @ts-ignore - ssm-session uses browser field mapping
import { ssm } from "ssm-session/dist/esm-browser";

export default function  SsmTerminal() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Priorizar el id de la URL, luego el serviceId de query params (para compatibilidad)
  const serviceId = id || searchParams.get('serviceId');
  
  const elRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasFetchedRef = useRef<string | null>(null); // Guardar el serviceId que ya se procesó
  const abortControllerRef = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<{ streamUrl: string; tokenValue: string } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [serviceInfo, setServiceInfo] = useState<{
    region?: string;
    name?: string;
    os?: string;
    instanceType?: string;
    ipAddress?: string;
    privateIp?: string;
    privateDnsName?: string;
    publicIp?: string | null;
    publicDnsName?: string | null;
    access?: 'PUBLIC_IP' | 'SSM';
    product?: any;
    status?: string;
  } | null>(null);

  // 1. Primero: Llamar a la API para obtener la sesión (no necesita el terminal)
  useEffect(() => {
    if (!serviceId) {
      setError("No se proporcionó un ID de servicio");
      setLoading(false);
      return;
    }

    // Prevenir llamadas duplicadas: si ya se procesó este serviceId, no hacer nada
    if (hasFetchedRef.current === serviceId) {
      return;
    }

    // Cancelar cualquier petición anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Crear nuevo AbortController para esta petición
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Marcar que estamos procesando este serviceId
    hasFetchedRef.current = serviceId;

    (async () => {
      try {
        // Obtener información completa del servicio
        try {
          const service: any = await servicesService.getById(serviceId);
          
          // Verificar si la petición fue cancelada
          if (abortController.signal.aborted) return;
          
          setServiceInfo({ 
            region: service.region,
            name: service.name,
            os: service.product?.os || service.os,
            instanceType: service.instanceType || service.metadata?.vmConfig?.instanceType,
            ipAddress: service.access === 'PUBLIC_IP' ? (service.publicIp || service.ipAddress) : null,
            privateIp: service.privateIp,
            privateDnsName: service.privateDnsName,
            publicIp: service.publicIp,
            publicDnsName: service.publicDnsName,
            access: service.access,
            product: service.product,
            status: service.status,
          });
        } catch (err: any) {
          if (abortController.signal.aborted) return;
          console.warn('No se pudo obtener información del servicio:', err);
        }
        
        const data = await servicesService.startSession(serviceId);
        
        // Verificar si la petición fue cancelada
        if (abortController.signal.aborted) return;
        
        if (!data.streamUrl || !data.tokenValue) {
          throw new Error('La respuesta de startSession no contiene streamUrl o tokenValue');
        }
        
        setSessionData({ streamUrl: data.streamUrl, tokenValue: data.tokenValue });
        setLoading(false);
      } catch (err: any) {
        // Ignorar errores de cancelación
        if (abortController.signal.aborted) return;
        
        console.error('Error en startSession:', err);
        const errorMessage = err?.message || String(err);
        setError(errorMessage);
        setLoading(false);
        // Resetear el flag solo en caso de error para permitir reintento
        if (hasFetchedRef.current === serviceId) {
          hasFetchedRef.current = null;
        }
      }
    })();

    // Cleanup: cancelar peticiones solo si el serviceId realmente cambia
    return () => {
      // Solo cancelar si el serviceId cambió (no en el primer cleanup de StrictMode)
      // El abortController se maneja automáticamente cuando se crea uno nuevo
    };
  }, [serviceId]);

  // 2. Segundo: Inicializar el terminal y conectarlo cuando tengamos la sesión Y el elemento esté disponible
  useEffect(() => {
    if (!elRef.current || !sessionData) return;

    // Guardar referencias locales para usar en los event listeners
    const currentServiceId = serviceId || 'N/A';
    const currentTenantId = user?.tenantId?.replace(/^tenant-/, '') || 'N/A';
    const currentRegion = serviceInfo?.region || 'N/A';
    const currentServiceName = serviceInfo?.name || 'N/A';
    const currentOS = serviceInfo?.os || serviceInfo?.product?.name || 'N/A';
    const currentInstanceType = serviceInfo?.instanceType || 'N/A';
    const currentPrivateIP = serviceInfo?.privateIp || 'N/A';
    const currentPublicIP = serviceInfo?.publicIp || 'N/A';
    const currentPrivateDNS = serviceInfo?.privateDnsName || 'N/A';
    const currentPublicDNS = serviceInfo?.publicDnsName || 'N/A';
    const currentAccess = serviceInfo?.access || 'N/A';

    const term = new Terminal({
      cursorBlink: true,
      convertEol: true,
      fontFamily: 'Consolas, "Courier New", monospace',
      fontSize: 14,
      lineHeight: 1.2,
      letterSpacing: 0,
      theme: {
        background: '#000000',
        foreground: '#ffffff',
        cursor: '#ffffff',
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    fitRef.current = fit;

    termRef.current = term;
    term.open(elRef.current);

    // Esperar a que el terminal esté completamente inicializado antes de hacer fit
    const fitTerminal = () => {
      try {
        if (fitRef.current && termRef.current) {
          fitRef.current.fit();
        }
      } catch (err) {
        console.error("Error fitting terminal:", err);
      }
    };

    // Usar doble requestAnimationFrame para asegurar que el DOM esté completamente renderizado
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fitTerminal();
      });
    });

    // Manejar resize de la ventana
    const handleResize = () => {
      fitTerminal();
    };
    window.addEventListener("resize", handleResize);

    // Ajustar terminal cuando cambie el tamaño del contenedor (para cuando se colapsa el panel)
    const resizeObserver = new ResizeObserver(() => {
      fitTerminal();
    });
    
    if (elRef.current?.parentElement) {
      resizeObserver.observe(elRef.current.parentElement);
    }

    const textDecoder = new TextDecoder();
    const textEncoder = new TextEncoder();

    term.writeln("Conectando a SSM...");

    // 3. Conectar el WebSocket con la información de la sesión
    const socket = new WebSocket(sessionData.streamUrl);
    socket.binaryType = "arraybuffer";
    socketRef.current = socket;

    socket.addEventListener("open", () => {
      try {
        // Asegurar que el terminal tenga dimensiones válidas
        if (fitRef.current) {
          fitRef.current.fit();
        }
        const termOptions = {
          rows: term.rows || 24,
          cols: term.cols || 80,
        };
        ssm.init(socket, {
          token: sessionData.tokenValue,
          termOptions: termOptions,
        });
        // Mostrar mensaje de bienvenida formateado
        term.writeln("\r\n");
        term.writeln("╔═══════════════════════════════════════════════════════════════╗");
        term.writeln("║                                                               ║");
        term.writeln("║     🚀  Welcome to your Kubiy Virtual Machine                ║");
        term.writeln("║                                                               ║");
        term.writeln("╚═══════════════════════════════════════════════════════════════╝");
        term.writeln("");
        term.writeln("┌─ Instance Information ─────────────────────────────────────┐");
        term.writeln(`│  Service Name:  ${currentServiceName.padEnd(45)}│`);
        term.writeln(`│  Service ID:    ${currentServiceId.padEnd(45)}│`);
        term.writeln(`│  Operating System: ${currentOS.padEnd(42)}│`);
        term.writeln(`│  Instance Type: ${currentInstanceType.padEnd(43)}│`);
        term.writeln(`│  Region:        ${currentRegion.padEnd(45)}│`);
        term.writeln(`│  Access Method: ${currentAccess.padEnd(43)}│`);
        term.writeln("└───────────────────────────────────────────────────────────────┘");
        term.writeln("");
        term.writeln("┌─ Network Information ───────────────────────────────────────┐");
        if (currentPrivateIP !== 'N/A') {
          term.writeln(`│  Private IP:    ${currentPrivateIP.padEnd(45)}│`);
        }
        if (currentPrivateDNS !== 'N/A') {
          term.writeln(`│  Private DNS:   ${currentPrivateDNS.padEnd(45)}│`);
        }
        if (currentAccess === 'PUBLIC_IP' && currentPublicIP !== 'N/A') {
          term.writeln(`│  Public IP:     ${currentPublicIP.padEnd(45)}│`);
        }
        if (currentAccess === 'PUBLIC_IP' && currentPublicDNS !== 'N/A') {
          term.writeln(`│  Public DNS:    ${currentPublicDNS.padEnd(45)}│`);
        }
        if (currentAccess === 'SSM') {
          term.writeln(`│  Access:        SSM (Secure Session Manager)${' '.padEnd(10)}│`);
        }
        term.writeln("└───────────────────────────────────────────────────────────────┘");
        term.writeln("");
        term.writeln("┌─ Account Information ────────────────────────────────────────┐");
        term.writeln(`│  Tenant ID:     ${currentTenantId.padEnd(45)}│`);
        term.writeln("└───────────────────────────────────────────────────────────────┘");
        term.writeln("");
        term.writeln("┌─ Important Notice ──────────────────────────────────────────┐");
        term.writeln("│                                                               │");
        term.writeln("│  This server is managed by Kubiy Cloud.                      │");
        term.writeln("│  Unauthorized access is prohibited.                          │");
        term.writeln("│                                                               │");
        term.writeln("└───────────────────────────────────────────────────────────────┘");
        term.writeln("");
        term.writeln("┌─ Support & Documentation ───────────────────────────────────┐");
        term.writeln("│  Documentation: https://kubiy.cloud/docs                      │");
        term.writeln("│  Support:       https://kubiy.cloud/support                   │");
        term.writeln("└───────────────────────────────────────────────────────────────┘");
        term.writeln("");
        term.writeln("Type 'help' for available commands or start working.\r\n");
      } catch (err: any) {
        term.writeln(`\r\n[ERROR] ${String(err?.message || err)}`);
      }
    });

    socket.addEventListener("message", (event) => {
      try {
        const agentMessage = ssm.decode(event.data);
        ssm.sendACK(socket, agentMessage);
        
        if (agentMessage.payloadType === 1) {
          // Output stream data
          term.write(textDecoder.decode(agentMessage.payload));
        } else if (agentMessage.payloadType === 17) {
          // Size change
          try {
            if (fitRef.current) {
              fitRef.current.fit();
            }
            const termOptions = {
              rows: term.rows || 24,
              cols: term.cols || 80,
            };
            ssm.sendInitMessage(socket, termOptions);
          } catch (err: any) {
            console.error("Error handling size change:", err);
          }
        }
      } catch (err: any) {
        term.writeln(`\r\n[ERROR] ${String(err?.message || err)}`);
      }
    });

    socket.addEventListener("error", (err) => {
      term.writeln(`\r\n[ERROR] ${String(err)}`);
    });

    socket.addEventListener("close", () => {
      term.writeln("\r\n[Disconnected]");
    });

    // teclado → enviar al canal
    term.onData((data) => {
      if (socket.readyState === WebSocket.OPEN) {
        ssm.sendText(socket, textEncoder.encode(data));
      }
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
      try {
        if (socketRef.current) {
          socketRef.current.close();
        }
      } catch {}
      try {
        if (termRef.current) {
          termRef.current.dispose();
        }
      } catch {}
      fitRef.current = null;
      termRef.current = null;
      socketRef.current = null;
    };
  }, [
    sessionData, 
    serviceId, 
    user?.tenantId, 
    serviceInfo?.region,
    serviceInfo?.name,
    serviceInfo?.os,
    serviceInfo?.instanceType,
    serviceInfo?.ipAddress,
    serviceInfo?.privateIp,
    serviceInfo?.privateDnsName,
    serviceInfo?.publicIp,
    serviceInfo?.publicDnsName,
    serviceInfo?.access,
    serviceInfo?.product?.name
  ]); // Ejecutar cuando tengamos la sesión o cambien los datos

  // Manejar pantalla completa
  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Error al cambiar modo pantalla completa:', err);
    }
  };

  // Escuchar cambios en el estado de pantalla completa
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/50 dark:bg-dark-bg py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="text-center">
          <LoadingOutlined className="text-4xl text-gray-400 dark:text-gray-500 mb-4" spin />
          <p className="text-gray-600 dark:text-gray-300">Iniciando sesión de terminal...</p>
          {serviceId && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Service ID: {serviceId}</p>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50/50 dark:bg-dark-bg py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Error al iniciar la sesión de terminal'}</p>
          {!serviceId && (
            <p className="text-sm text-gray-500 dark:text-gray-400">No se proporcionó un ID de servicio</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`min-h-screen bg-gray-50/50 dark:bg-dark-bg ${isFullscreen ? 'py-0 px-0' : 'py-6 px-4 sm:px-6 lg:px-8'}`}
    >
      <div className={`${isFullscreen ? 'h-screen' : 'max-w-[1600px] mx-auto'}`}>
        {/* Header con información del servicio - Oculto en pantalla completa */}
        {!isFullscreen && (
            <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeftOutlined />
                <span className="text-sm font-medium">Volver</span>
              </button>
            </div>
            
            {/* Panel de información del servicio */}
            {serviceInfo && (
              <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    {serviceInfo.product?.images?.[0] && !imgError ? (
                      <img
                        src={serviceInfo.product.images[0]}
                        alt={serviceInfo.product.name || 'OS'}
                        className="w-8 h-8 rounded-lg object-cover opacity-60"
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center text-white text-sm">
                        💻
                      </div>
                    )}
                    <div>
                      <h2 className="text-base font-bold text-gray-900 dark:text-white">
                        {serviceInfo.name || 'Virtual Machine Terminal'}
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400">SSH Terminal Session</p>
                    </div>
                  </div>
                </div>
                
                {/* Contenido */}
                <div className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Service ID</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-white font-mono break-all">{serviceId || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Operating System</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-white">
                        {serviceInfo.os || serviceInfo.product?.name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Instance Type</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-white">{serviceInfo.instanceType || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Region</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-white">{serviceInfo.region || 'N/A'}</p>
                    </div>
                    {serviceInfo.status && (
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          serviceInfo.status === 'running' ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' :
                          serviceInfo.status === 'stopped' ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300' :
                          serviceInfo.status === 'deploying' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300' :
                          'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300'
                        }`}>
                          {serviceInfo.status.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Network Information Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">Network Information</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {serviceInfo.privateIp && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Private IP</p>
                          <p className="text-xs font-medium text-gray-900 dark:text-white font-mono">{serviceInfo.privateIp}</p>
                        </div>
                      )}
                      {serviceInfo.privateDnsName && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Private DNS</p>
                          <p className="text-xs font-medium text-gray-900 dark:text-white font-mono break-all">{serviceInfo.privateDnsName}</p>
                        </div>
                      )}
                      {serviceInfo.access === 'PUBLIC_IP' && serviceInfo.publicIp && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Public IP</p>
                          <p className="text-xs font-medium text-gray-900 dark:text-white font-mono">{serviceInfo.publicIp}</p>
                        </div>
                      )}
                      {serviceInfo.access === 'PUBLIC_IP' && serviceInfo.publicDnsName && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Public DNS</p>
                          <p className="text-xs font-medium text-gray-900 dark:text-white font-mono break-all">{serviceInfo.publicDnsName}</p>
                        </div>
                      )}
                      {serviceInfo.access && (
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Access Method</p>
                          <p className="text-xs font-medium text-gray-900 dark:text-white">
                            {serviceInfo.access === 'PUBLIC_IP' ? 'Public IP' : 'SSM'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Terminal */}
        <div className={`bg-white dark:bg-dark-bg ${isFullscreen ? 'h-screen rounded-none' : 'rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm'} overflow-hidden`}>
          <div className="px-4 py-2 bg-gray-900 dark:bg-gray-950 border-b border-gray-700 dark:border-gray-800 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <div className="flex-1 text-center">
              <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">ssh@{serviceInfo?.name || 'vm'}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleFullscreen}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-200 dark:hover:text-gray-300 transition-colors p-1"
                title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
              >
                {isFullscreen ? (
                  <FullscreenExitOutlined className="text-sm" />
                ) : (
                  <FullscreenOutlined className="text-sm" />
                )}
              </button>
              <InfoCircleOutlined className="text-gray-400 dark:text-gray-500 text-sm" />
            </div>
          </div>
          <div 
            ref={elRef} 
            style={{ 
              height: isFullscreen ? "calc(100vh - 40px)" : "calc(100vh - 280px)", 
              padding: 0,
              overflow: "hidden",
              position: "relative",
              backgroundColor: "#000000"
            }} 
          />
        </div>
      </div>
    </div>
  );
}
